export type ActivityLocale = 'en' | 'hr' | 'pl';

const en: Record<string, string> = {
  'activity.notification.sent.title': '{search}',
  'activity.notification.sent.summary': 'Notification sent for {search}',
  'activity.notification.failed.title': 'Notification failed',
  'activity.notification.failed.summary': 'Could not deliver alert for {search}',
  'activity.notification.skipped.title': '{search}',
  'activity.notification.skipped.summary': 'Skipped — {reason}',
  'activity.notification.recipient_changed.title': 'Recipient updated',
  'activity.notification.recipient_changed.summary': 'Recipient visibility or settings changed',
  'activity.result.listing_found.title': '{search}',
  'activity.result.listing_found.summary': 'New listing: {title}',
  'activity.search.created.title': 'Search created',
  'activity.search.created.summary': '{search} is ready to monitor',
  'activity.subscription.created.title': 'Subscription created',
  'activity.subscription.created.summary': 'A search subscription was added',
  'activity.search.search_transferred.title': 'Search transferred',
  'activity.search.search_transferred.summary': 'Ownership changed',
  'activity.search.search_acl_changed.title': 'Search access updated',
  'activity.search.search_acl_changed.summary': 'Sharing permissions changed',
  'activity.search.template_installed.title': 'Template installed',
  'activity.search.template_installed.summary': '{search} was created from {template}',
  'activity.search.template_duplicated.title': 'Template duplicated',
  'activity.search.template_duplicated.summary': '{template} was duplicated to your workspace',
  'activity.search.template_favorite_added.title': 'Template favorited',
  'activity.search.template_favorite_added.summary': '{template} was added to your favorites',
  'activity.search.template_favorite_removed.title': 'Template unfavorited',
  'activity.search.template_favorite_removed.summary': '{template} was removed from your favorites',
  'activity.search.template_imported.title': 'Template imported',
  'activity.search.template_imported.summary': '{template} was imported into your workspace',
  'activity.search.template_exported.title': 'Template exported',
  'activity.search.template_exported.summary': '{template} configuration was exported',
  'activity.search.org_template_created.title': 'Workspace template created',
  'activity.search.org_template_created.summary': '{template} was added to My Templates',
  'activity.search.org_template_updated.title': 'Workspace template updated',
  'activity.search.org_template_updated.summary': '{template} was updated',
  'activity.search.template_published.title': 'Template published',
  'activity.search.template_published.summary': '{template} is now available to your team',
  'activity.search.template_archived.title': 'Template archived',
  'activity.search.template_archived.summary': '{template} was archived — installed searches are unaffected',
  'activity.search.paused.title': '{search} paused',
  'activity.search.paused.summary': '{reason}',
  'activity.team.invitation_sent.title': 'Invitation sent',
  'activity.team.invitation_sent.summary': 'A team invitation was sent',
  'activity.team.invitation_accepted.title': 'Invitation accepted',
  'activity.team.invitation_accepted.summary': 'A new member joined the workspace',
  'activity.team.invitation_revoked.title': 'Invitation revoked',
  'activity.team.invitation_revoked.summary': 'A pending invitation was cancelled',
  'activity.team.member_joined.title': 'Member joined',
  'activity.team.member_joined.summary': 'A new member joined the workspace',
  'activity.team.member_removed.title': 'Member removed',
  'activity.team.member_removed.summary': 'A member was removed from the workspace',
  'activity.team.member_left.title': 'Member left',
  'activity.team.member_left.summary': 'A member left the workspace',
  'activity.team.role_changed.title': 'Role changed',
  'activity.team.role_changed.summary': 'A member role was updated',
  'activity.team.ownership_transferred.title': 'Ownership transferred',
  'activity.team.ownership_transferred.summary': 'Workspace ownership changed',
  'activity.system.settings_changed.title': 'Settings updated',
  'activity.system.settings_changed.summary': 'Workspace settings were updated',
  'activity.attention.invitations.title': 'Pending invitations',
  'activity.attention.invitations.summary': '{count} invitation(s) awaiting acceptance',
  'activity.page.title': 'Activity',
  'activity.page.subtitle': 'What Scoutly has done for your workspace',
  'activity.filter.category': 'Category',
  'activity.filter.severity': 'Severity',
  'activity.filter.unread': 'Unread only',
  'activity.filter.mine': 'Mine only',
  'activity.empty.title': 'No activity yet',
  'activity.empty.description': 'Create a search and run a scrape to see your timeline.',
  'activity.empty.action': 'Create search',
  'activity.attention.title': 'Needs attention',
  'activity.recent.title': 'Recent activity',
  'activity.viewAll': 'View all',
  'activity.attention.empty.title': 'No actions required.',
  'activity.attention.empty.description': 'Everything looks healthy.',
  'activity.action.open': 'Open',
  'activity.action.renew': 'Renew',
  'activity.action.retry': 'Retry',
  'activity.markAllRead': 'Mark all read',
};

const hr: Record<string, string> = {
  ...en,
  'activity.page.title': 'Aktivnost',
  'activity.page.subtitle': 'Što je Scoutly napravio za vaš workspace',
  'activity.empty.title': 'Još nema aktivnosti',
};

const pl: Record<string, string> = {
  ...en,
  'activity.page.title': 'Aktywność',
  'activity.page.subtitle': 'Co Scoutly zrobił dla Twojego workspace',
  'activity.empty.title': 'Brak aktywności',
};

const catalogs: Record<ActivityLocale, Record<string, string>> = { en, hr, pl };

let currentLocale: ActivityLocale = 'en';

export function setActivityLocale(locale: ActivityLocale) {
  currentLocale = locale;
}

export function tActivity(key: string, params?: Record<string, string | number>): string {
  const template = catalogs[currentLocale][key] ?? catalogs.en[key] ?? key;
  if (!params) return template;
  return Object.entries(params).reduce(
    (text, [k, v]) => text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v)),
    template,
  );
}

export function resolveActivityText(
  item: {
    titleKey?: string | null;
    summaryKey?: string | null;
    title?: string | null;
    summary?: string | null;
    titleParams?: Record<string, unknown>;
    summaryParams?: Record<string, unknown>;
  },
): { title: string; summary: string } {
  const titleParams = Object.fromEntries(
    Object.entries(item.titleParams || {}).map(([k, v]) => [k, String(v ?? '')]),
  );
  const summaryParams = Object.fromEntries(
    Object.entries({
      ...Object.fromEntries(Object.entries(item.titleParams || {}).map(([k, v]) => [k, String(v ?? '')])),
      ...(item.summaryParams || {}),
    }).map(([k, v]) => [k, String(v ?? '')]),
  );
  const title = item.title?.trim()
    || (item.titleKey ? tActivity(item.titleKey, titleParams) : 'Activity');
  const summary = item.summary?.trim()
    || (item.summaryKey ? tActivity(item.summaryKey, summaryParams) : '');
  return { title, summary };
}
